import BottomSheet from '@rio-cloud/rio-uikit/BottomSheet';
import Button from '@rio-cloud/rio-uikit/Button';
import { useEffect, useRef, useState } from 'react';

import { type DemoCommand, type PickupPreset, type Signal, usePublishSignalMutation } from '../../services/scmMockApi';
import { useTransportContext } from '../transports/TransportContext';
import { ChainPresets, demoSignals as signals } from './DemoSignals';
import {
    chainFrozen,
    chainName,
    commandFor,
    type DemoState,
    demoStorageKey,
    type EventChain,
    emptyDemoState,
    loadDemoState,
    publicationKey,
    publishedRequests,
    publishedTransports,
    requestNeedsPublication,
} from './demoState';
import ImportDemoScenario from './ImportDemoScenario';
import ResetDemoData from './ResetDemoData';

import './demoControls.css';

type DemoControlsProps = { onHeightChange: (height: number) => void };

export default function DemoControls(props: DemoControlsProps) {
    const { refreshAfterSignal } = useTransportContext();
    return <DemoControlsPanel {...props} onDataChanged={refreshAfterSignal} refreshMessage='Table refresh requested' />;
}

export function DemoControlsPanel({
    onHeightChange,
    onDataChanged,
    refreshMessage = 'View refresh requested',
}: DemoControlsProps & { onDataChanged: () => void; refreshMessage?: string }) {
    const [initial] = useState(() => {
        try {
            return { state: loadDemoState(), error: '' };
        } catch {
            return {
                state: emptyDemoState(),
                error: 'Saved demo controls could not be read. Reload after restoring browser storage.',
            };
        }
    });
    const [state, setState] = useState(initial.state);
    const stateRef = useRef(state);
    const [error, setError] = useState(initial.error);
    const [expanded, setExpanded] = useState(false);
    const [clearing, setClearing] = useState(false);
    const [importing, setImporting] = useState(false);
    const [busy, setBusy] = useState<string[]>([]);
    const busyRef = useRef(new Set<string>());
    const [message, setMessage] = useState('');
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const toggleRef = useRef<HTMLButtonElement | null>(null);
    const [send] = usePublishSignalMutation();
    const chain = state.chains.find(value => value.id === state.selectedId);
    const ActiveSignal = signals.find(signal => signal.id === state.tab)?.component ?? signals[0].component;
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    useEffect(() => {
        const sheet = bodyRef.current?.closest('.bottom-sheet');
        if (!sheet) {
            return;
        }
        const measure = () => onHeightChange(Math.ceil(sheet.getBoundingClientRect().height));
        const observer = new ResizeObserver(measure);
        observer.observe(sheet);
        measure();
        return () => observer.disconnect();
    }, [onHeightChange]);
    const commit = (next: DemoState) => {
        localStorage.setItem(demoStorageKey, JSON.stringify(next));
        stateRef.current = next;
        if (mounted.current) {
            setState(next);
        }
    };
    const update = (changeState: (current: DemoState) => DemoState) => {
        try {
            commit(changeState(stateRef.current));
        } catch {
            setError('Could not save demo controls. Check browser storage before publishing.');
        }
    };
    const modifyChain = (id: string, changeChain: (current: EventChain) => EventChain) => {
        const current = stateRef.current;
        commit({ ...current, chains: current.chains.map(value => (value.id === id ? changeChain(value) : value)) });
    };
    const addChain = () => {
        update(current => {
            const next: EventChain = {
                id: crypto.randomUUID(),
                name: chainName(current.nextOrdinal),
                createdAt: new Date().toISOString(),
                datePreset: 'standard',
                count: 1,
                requestCount: 1,
                pickupPreset: 'matching',
                publications: {},
            };
            return {
                ...current,
                selectedId: next.id,
                chains: [...current.chains, next],
                nextOrdinal: current.nextOrdinal + 1,
            };
        });
    };
    const selectTab = (tab: Signal) => update(current => ({ ...current, tab }));
    const publish = async (signal: Signal, all = false, pickup: PickupPreset = 'matching') => {
        if (!chain || error || clearing || importing || busyRef.current.has(chain.id)) {
            return;
        }
        const chainId = chain.id;
        busyRef.current.add(chainId);
        setBusy([...busyRef.current]);
        setMessage('Publishing…');
        try {
            const indices = Array.from(
                { length: signal === 'request' ? chain.requestCount : chain.count },
                (_, i) => i + 1
            ).filter(index =>
                signal === 'request'
                    ? requestNeedsPublication(chain, index)
                    : !chain.publications[publicationKey(signal, index)]?.receipt
            );
            for (const index of all ? indices : indices.slice(0, 1)) {
                const key = publicationKey(signal, index);
                const current = stateRef.current.chains.find(value => value.id === chainId);
                if (!current) {
                    break;
                }
                const command: DemoCommand =
                    current.publications[key] && !current.publications[key].receipt
                        ? current.publications[key].command
                        : {
                              ...commandFor(current, index, pickup, signal),
                              revision:
                                  signal === 'request' ? (current.publications[key]?.receipt?.revision ?? 0) + 1 : 1,
                          };
                modifyChain(chainId, value => ({
                    ...value,
                    publications: { ...value.publications, [key]: { command } },
                }));
                try {
                    const receipt = await send({ signal, command }).unwrap();
                    modifyChain(chainId, value => ({
                        ...value,
                        publications: { ...value.publications, [key]: { command, receipt } },
                    }));
                    onDataChanged();
                } catch (failure) {
                    const status =
                        typeof failure === 'object' && failure !== null && 'status' in failure
                            ? failure.status
                            : undefined;
                    const detail =
                        status === 403 || status === 401
                            ? 'Sign in with an account that can access the Control Tower transport view.'
                            : 'Publication was not confirmed. Retry the original command.';
                    modifyChain(chainId, value => ({
                        ...value,
                        publications: { ...value.publications, [key]: { command, error: detail } },
                    }));
                    setMessage('');
                    return;
                }
            }
            setMessage(`Published for ${chain.name} · ${refreshMessage}`);
        } catch {
            setError(
                'Could not save publication progress. Restore browser storage before retrying the original command.'
            );
        } finally {
            busyRef.current.delete(chainId);
            if (mounted.current) {
                setBusy([...busyRef.current]);
            }
        }
    };
    return (
        <BottomSheet
            show
            title={
                <button
                    type='button'
                    ref={toggleRef}
                    className='scm-demo-toggle'
                    aria-label='Demo controls'
                    aria-expanded={expanded}
                    aria-controls='scm-demo-content'
                    disabled={importing}
                    onClick={() => setExpanded(value => !value)}
                >
                    <span>Demo controls</span>
                    <span className='scm-demo-toggle-meta'>
                        {state.chains.length > 0 &&
                            `${state.chains.length} event chain${state.chains.length === 1 ? '' : 's'}`}{' '}
                        <span aria-hidden='true'>{expanded ? '⌄' : '⌃'}</span>
                    </span>
                </button>
            }
            showCloseButton={false}
            hasBackdrop={false}
            showMaximizeButton={false}
            width='100%'
            className='scm-demo-sheet'
            bodyRef={bodyRef}
        >
            {expanded && (
                <section
                    id='scm-demo-content'
                    aria-label='Demo controls'
                    className='scm-demo-content'
                    onKeyDown={event => {
                        if (
                            event.key === 'Escape' &&
                            !importing &&
                            !(event.target instanceof HTMLElement && event.target.closest('[role="dialog"]'))
                        ) {
                            setExpanded(false);
                            toggleRef.current?.focus();
                        }
                    }}
                >
                    <aside className='scm-demo-chains'>
                        <div className='scm-demo-heading'>
                            <h2>Event chains</h2>
                            <Button disabled={!!error || clearing || importing} onClick={addChain}>
                                + New
                            </Button>
                        </div>
                        <div className='scm-demo-chain-list'>
                            {state.chains.map(value => (
                                <button
                                    type='button'
                                    key={value.id}
                                    className='scm-demo-chain'
                                    aria-pressed={value.id === chain?.id}
                                    onClick={() => {
                                        update(current => ({ ...current, selectedId: value.id }));
                                        setMessage('');
                                    }}
                                >
                                    <strong>{value.name}</strong>
                                    <span className='scm-demo-chain-status'>
                                        <span>
                                            {publishedRequests(value)}/{value.requestCount} requests
                                        </span>
                                        <span>
                                            {publishedTransports(value)}/{value.count} transports
                                        </span>
                                    </span>
                                </button>
                            ))}
                        </div>
                        <ImportDemoScenario
                            disabled={!!error || clearing || busy.length > 0}
                            onBusyChange={setImporting}
                            onDataChanged={onDataChanged}
                        />
                        <ResetDemoData
                            disabled={busy.length > 0 || importing}
                            onBusyChange={setClearing}
                            onCleared={() => {
                                commit(emptyDemoState());
                                setError('');
                                setMessage('All demo data cleared');
                                onDataChanged();
                            }}
                        />
                    </aside>
                    <div className='scm-demo-signals'>
                        {error && <p role='alert'>{error}</p>}
                        {chain ? (
                            <>
                                <div className='scm-demo-heading'>
                                    <h2>{chain.name}</h2>
                                    <span className='scm-demo-chain-context'>
                                        {chainFrozen(chain) && (
                                            <span className='scm-demo-locked'>
                                                <span className='rioglyph rioglyph-lock' aria-hidden='true' /> Plan
                                                locked
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <ChainPresets
                                    chain={chain}
                                    change={patch => {
                                        if (!chainFrozen(chain)) {
                                            update(current => ({
                                                ...current,
                                                chains: current.chains.map(value =>
                                                    value.id === chain.id ? { ...value, ...patch } : value
                                                ),
                                            }));
                                        }
                                    }}
                                />
                                <div className='scm-demo-signal-nav'>
                                    <h3>Signals</h3>
                                    <div role='tablist' aria-label='Signals' className='scm-demo-tabs'>
                                        {signals.map((signal, i) => (
                                            <button
                                                type='button'
                                                role='tab'
                                                key={signal.id}
                                                id={`scm-${signal.id}-tab`}
                                                aria-controls={`scm-${signal.id}-panel`}
                                                aria-selected={state.tab === signal.id}
                                                onClick={() => selectTab(signal.id)}
                                                onKeyDown={event => {
                                                    const next =
                                                        event.key === 'Home'
                                                            ? 0
                                                            : event.key === 'End'
                                                              ? signals.length - 1
                                                              : event.key === 'ArrowRight'
                                                                ? (i + 1) % signals.length
                                                                : event.key === 'ArrowLeft'
                                                                  ? (i + signals.length - 1) % signals.length
                                                                  : undefined;
                                                    if (next !== undefined) {
                                                        event.preventDefault();
                                                        selectTab(signals[next].id);
                                                        document.getElementById(`scm-${signals[next].id}-tab`)?.focus();
                                                    }
                                                }}
                                            >
                                                {signal.label}
                                                <span>
                                                    {signal.id === 'request'
                                                        ? `${publishedRequests(chain)} / ${chain.requestCount}`
                                                        : `${publishedTransports(chain)} / ${chain.count}`}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div
                                    role='tabpanel'
                                    id={`scm-${state.tab}-panel`}
                                    aria-labelledby={`scm-${state.tab}-tab`}
                                >
                                    <ActiveSignal
                                        key={`${chain.id}:${state.tab}`}
                                        chain={chain}
                                        busy={busy.includes(chain.id) || !!error || clearing || importing}
                                        change={patch =>
                                            update(current => ({
                                                ...current,
                                                chains: current.chains.map(value =>
                                                    value.id === chain.id
                                                        ? {
                                                              ...value,
                                                              ...(chainFrozen(value)
                                                                  ? {
                                                                        pickupPreset:
                                                                            patch.pickupPreset ?? value.pickupPreset,
                                                                        requestStatus:
                                                                            patch.requestStatus ?? value.requestStatus,
                                                                    }
                                                                  : patch),
                                                          }
                                                        : value
                                                ),
                                            }))
                                        }
                                        selectTab={selectTab}
                                        publish={publish}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className='scm-demo-empty'>
                                <span className='scm-demo-empty-icon' aria-hidden='true'>
                                    ↗
                                </span>
                                <div>
                                    <strong>Your next scenario starts here</strong>
                                    <p>Create an event chain, then publish either signal first.</p>
                                </div>
                                <Button bsStyle={Button.PRIMARY} disabled={!!error || clearing} onClick={addChain}>
                                    Create event chain
                                </Button>
                            </div>
                        )}
                        <p className='scm-demo-hint' role='status' aria-live='polite'>
                            {message}
                        </p>
                    </div>
                </section>
            )}
        </BottomSheet>
    );
}
